import React, { useState } from "react";
import { Card, Upload, Button, message, Checkbox, Space, Typography } from "antd";
import { UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import { Booking } from "../../types/booking";
import {
  uploadCommercialInvoice,
  downloadCommercialInvoice,
} from "../../utils/commercialInvoiceUtils";
import { updateBookingCommercialInvoice } from "../../services/bookingService";
import { AgentCommercialInvoicePreview } from "./AgentCommercialInvoicePreview";

const { Text } = Typography;

interface AgentCommercialInvoiceProps {
  booking: Booking;
  onUpdated: () => void;
}

export const AgentCommercialInvoice: React.FC<AgentCommercialInvoiceProps> = ({
  booking,
  onUpdated,
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [appendInvoice, setAppendInvoice] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!booking.commercial_invoice_file) {
      message.warning("No commercial invoice on file");
      return;
    }
    setDownloading(true);
    try {
      await downloadCommercialInvoice(
        booking.commercial_invoice_file,
        `commercial-invoice-${booking.id}`,
        booking.id
      );
    } catch (err) {
      console.error(err);
      message.error("Failed to download invoice");
    } finally {
      setDownloading(false);
    }
  };

  const handleUpload = async () => {
    const file = fileList[0]?.originFileObj;
    if (!file) {
      message.warning("Select a file first");
      return;
    }
    setUploading(true);
    try {
      const uploadResult = await uploadCommercialInvoice(
        booking.id,
        file,
        appendInvoice,
        booking.company_id
      );
      if (!uploadResult || uploadResult.error || !uploadResult.path) {
        throw uploadResult?.error || new Error("Upload failed");
      }
      const result = await updateBookingCommercialInvoice(
        booking.id,
        uploadResult.path,
        appendInvoice
      );
      if (result.error) throw result.error;
      message.success("Commercial invoice uploaded");
      setFileList([]);
      onUpdated();
    } catch (err) {
      console.error(err);
      message.error("Failed to upload invoice");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card size="small" title="Commercial invoice">
      {booking.commercial_invoice_file ? (
        <>
          <AgentCommercialInvoicePreview
            commercialInvoiceFile={booking.commercial_invoice_file}
            bookingId={booking.id}
            onDownload={handleDownload}
            downloading={downloading}
          />
          <Button
            type="primary"
            block
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            loading={downloading}
            style={{ marginTop: 8 }}
          >
            Download
          </Button>
        </>
      ) : (
        <div
          style={{
            minHeight: 80,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px dashed #d9d9d9",
            borderRadius: 8,
            padding: 12,
            marginBottom: 8,
          }}
        >
          <Text type="secondary" style={{ fontSize: 12, textAlign: "center" }}>
            No commercial invoice uploaded yet
          </Text>
        </div>
      )}

      <Space direction="vertical" style={{ width: "100%", marginTop: 8 }} size="small">
        <Upload
          beforeUpload={() => false}
          fileList={fileList}
          onChange={({ fileList: fl }) => setFileList(fl.slice(-1))}
          accept=".pdf,.PDF,.xlsx,.xls"
          maxCount={1}
        >
          <Button block icon={<UploadOutlined />}>
            Select file
          </Button>
        </Upload>
        <Checkbox
          checked={appendInvoice}
          onChange={(e) => setAppendInvoice(e.target.checked)}
        >
          Append to existing invoice
        </Checkbox>
        <Button
          block
          onClick={handleUpload}
          loading={uploading}
          disabled={fileList.length === 0}
        >
          Upload invoice
        </Button>
      </Space>
    </Card>
  );
};
